import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Mail, Phone, Menu, X } from 'lucide-react';
import './greenline.css';
import { COMPANY } from './content';

const NAV = [
  { to: '/', label: 'Home', end: true },
  { to: '/kitchens', label: 'Kitchens' },
  { to: '/bathrooms', label: 'Bathrooms' },
  { to: '/carpentry', label: 'Carpentry' },
  { to: '/basements', label: 'Basements' },
  { to: '/contact', label: 'Get a Quote' },
];

// Intersection-observer driven reveal animations. Any element with
// `.gl-reveal` fades + lifts into place as it scrolls into view.
function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.greenline-root .gl-reveal');
    if (!('IntersectionObserver' in window) || els.length === 0) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
    }, { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  });
}

export default function GreenlineLayout() {
  useScrollReveal();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const lastPathRef = useRef(location.pathname);

  // Close mobile menu + reset scroll on route change.
  useEffect(() => {
    if (lastPathRef.current !== location.pathname) {
      lastPathRef.current = location.pathname;
      setOpen(false);
      if (ref.current) ref.current.scrollTo({ top: 0, behavior: 'instant' });
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  });

  return (
    <div ref={ref} className="greenline-root">
      <header className="gl-nav">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="brand-logo">
            <img src={COMPANY.logo} alt="Greenline Construction" className="h-12 md:h-14 w-auto" />
          </Link>
          <nav className="hidden md:flex items-center gap-10">
            {NAV.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `text-[0.78rem] uppercase tracking-[0.18em] transition-colors ${isActive ? 'text-white' : 'text-[color:var(--gl-muted)] hover:text-white'} ${item.label === 'Get a Quote' ? 'border border-white/20 px-4 py-2 hover:border-[color:var(--gl-primary)]' : ''}`
                }>
                {item.label}
              </NavLink>
            ))}
          </nav>
          <button onClick={() => setOpen((v) => !v)} className="md:hidden text-white p-2 -mr-2" aria-label="Toggle menu" data-testid="mobile-menu-toggle">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-white/10 bg-[color:var(--gl-bg)]/95" data-testid="mobile-menu">
            {NAV.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end}
                className={({ isActive }) =>
                  `block px-6 py-4 text-sm uppercase tracking-[0.18em] border-b border-white/5 ${isActive ? 'text-white' : 'text-[color:var(--gl-muted)]'}`
                }>
                {item.label}
              </NavLink>
            ))}
          </div>
        )}
      </header>

      <main className="pt-0"><Outlet /></main>

      <Link to="/contact" className="gl-sticky-cta" data-testid="mobile-sticky-cta">Get a Free Estimate →</Link>

      <footer className="border-t border-white/10 pt-16 pb-10 mt-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-4 gap-10">
          <div>
            <img src={COMPANY.logo} alt="Greenline Construction" className="h-16 w-auto" />
            <p className="text-sm text-[color:var(--gl-muted)] mt-6 leading-relaxed">{COMPANY.tagline}. A small studio of Massachusetts-trained carpenters delivering award-grade kitchens, baths, and finish work.</p>
          </div>
          <div>
            <div className="gl-overline mb-4">Visit</div>
            <p className="text-sm text-white leading-loose">{COMPANY.address}</p>
          </div>
          <div>
            <div className="gl-overline mb-4">Contact</div>
            <ul className="space-y-2 text-sm text-white">
              <li className="flex items-center gap-2"><Phone size={14} className="text-[color:var(--gl-primary)]" /><a href={COMPANY.phoneHref}>{COMPANY.phone}</a></li>
              <li className="flex items-center gap-2"><Mail size={14} className="text-[color:var(--gl-primary)]" /><a href={COMPANY.emailHref}>{COMPANY.email}</a></li>
            </ul>
          </div>
          <div>
            <div className="gl-overline mb-4">Elsewhere</div>
            <ul className="space-y-2 text-sm text-white">
              <li className="flex items-center gap-2"><span className="text-[color:var(--gl-primary)] text-xs">◇</span><a href={COMPANY.social.houzz} target="_blank" rel="noreferrer">Houzz</a></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 mt-12 pt-6 border-t border-white/5 text-xs text-[color:var(--gl-muted)]">
          © {new Date().getFullYear()} Greenline Construction. MA HIC #218099 · BBB Accredited (A Rating).
        </div>
      </footer>
    </div>
  );
}
